import dayjs from "dayjs";

export type DateRangeFilter =
  | "today"
  | "yesterday"
  | "this-week"
  | "this-month"
  | "custom";

/**
 * Gets the start/end of the current week (Monday to Sunday)
 */
export const getWeekBoundaries = (): { from: Date; to: Date } => {
  const now = dayjs();
  const dayOfWeek = now.day();
  // dayjs .day() returns 0=Sunday, 1=Monday, ... 6=Saturday
  // We want Monday as start of week
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const from = now.add(mondayOffset, "day").startOf("day").toDate();
  const to = dayjs(from).add(6, "day").endOf("day").toDate();
  return { from, to };
};

/**
 * Gets month boundaries based on a custom start day
 * e.g., monthStartDay=25 means month runs from 25th to 24th of next month
 */
export const getMonthBoundaries = (
  monthStartDay: number
): { from: Date; to: Date } => {
  const now = dayjs();
  const currentDay = now.date();
  let targetMonth: dayjs.Dayjs;

  if (currentDay >= monthStartDay) {
    // We're in the current month's cycle
    targetMonth = now;
  } else {
    // We're in the previous month's cycle
    targetMonth = now.subtract(1, "month");
  }

  // Clamp monthStartDay to valid days in the target month
  const daysInTargetMonth = targetMonth.daysInMonth();
  const clampedDay = Math.min(monthStartDay, daysInTargetMonth);

  const from = targetMonth.date(clampedDay).startOf("day");
  const to = from.add(1, "month").subtract(1, "day").endOf("day");
  return { from: from.toDate(), to: to.toDate() };
};

/**
 * Returns date_from and date_to strings for the given filter type
 */
export const getDateRangeForFilter = (
  type: DateRangeFilter,
  monthStartDay = 1
): { from: string; to: string } => {
  const format = "YYYY-MM-DD";

  switch (type) {
    case "today":
      return {
        from: dayjs().format(format),
        to: dayjs().format(format),
      };
    case "yesterday":
      return {
        from: dayjs().subtract(1, "day").format(format),
        to: dayjs().subtract(1, "day").format(format),
      };
    case "this-week": {
      const week = getWeekBoundaries();
      return {
        from: dayjs(week.from).format(format),
        to: dayjs(week.to).format(format),
      };
    }
    case "this-month": {
      const month = getMonthBoundaries(monthStartDay);
      return {
        from: dayjs(month.from).format(format),
        to: dayjs(month.to).format(format),
      };
    }
    case "custom":
      // Custom range must be provided by the caller; fall back to today
      // if called before the custom range is applied.
      return {
        from: dayjs().format(format),
        to: dayjs().format(format),
      };
    default:
      return {
        from: dayjs().format(format),
        to: dayjs().format(format),
      };
  }
};

/**
 * Checks if the selected date range is "today"
 */
export const isToday = (
  dateRange: DateRangeFilter,
  customRange?: [Date | null, Date | null]
): boolean => {
  if (dateRange === "today") return true;
  if (dateRange === "custom" && customRange) {
    const today = dayjs().format("YYYY-MM-DD");
    const from = customRange[0]
      ? dayjs(customRange[0]).format("YYYY-MM-DD")
      : null;
    const to = customRange[1]
      ? dayjs(customRange[1]).format("YYYY-MM-DD")
      : null;
    return from === today && to === today;
  }
  return false;
};

/**
 * Returns just the date portion (without "Showing data for:" prefix)
 */
export const formatDateLabel = (from: string, to: string): string => {
  if (from === to) {
    return dayjs(from).format("dddd, MMMM D, YYYY");
  }
  return `${dayjs(from).format("MMMM D")} - ${dayjs(to).format(
    "MMMM D, YYYY"
  )}`;
};
